import './styles/Select.scss';

import { CaretDown, Check } from '@phosphor-icons/react';
import React, {
  Children,
  HTMLAttributes,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../../../helpers/classBuilders';
import { useClassHelper } from '../../../../hooks/useClassHelper';
import { InputField } from '../../../InputField';
import { Loading } from '../../../Loading';
import { Paragraph } from '../../../Typography';
import SelectOption from './SelectOption';

const variants = {
  default: '--default',
  table: '--table',
  grouped: '--grouped',
} as const;

interface IOption {
  id: string;
  description: string;
}

interface ITranslator {
  exists: (key: string) => boolean;
  t: (key: string) => string;
}

/**
 * Props shared by every variant.
 */
type CommonProps = {
  children?: React.ReactNode;
  fetchFunction?: () => Promise<IOption[]>;
  updateFunction?: (value: string) => void;
  loadingText?: string;
  errorFetchingText?: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  native?: boolean;
  placeholder?: string;
  disabled?: boolean;
  i18n?: ITranslator;
  defaultValue?: string;
  value?: string;
  name?: string;
  dataCy?: string;
  // Fires when focus leaves the whole control (trigger + popover). The hidden
  // native <select> is aria-hidden/tabIndex=-1 so it never blurs on its own;
  // we synthesize this from the visible control instead.
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
} & Omit<HTMLAttributes<HTMLSelectElement>, 'onChange' | 'onBlur'>;

/**
 * Default variant — the only one that renders the minimalist control and
 * therefore the only one allowed to receive `hasSearch` / `loading`.
 */
type DefaultVariantProps = CommonProps & {
  variant?: 'default';
  hasSearch?: boolean;
  loading?: boolean;
  // The minimalist control renders its own label + error so consumers don't
  // have to wrap it with InputField.Label / InputField.ErrorMessage.
  label?: React.ReactNode;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  // Opt-in: renders the popover through a portal into document.body,
  // fixed-positioned from the trigger's bounding rect. Use this when the
  // control lives inside an ancestor with overflow-x/overflow-y set to
  // something other than visible (e.g. a horizontally-scrollable table) —
  // otherwise the popover's overflow gets pulled into that ancestor's
  // scrollable area and creates unwanted scroll instead of just overlaying.
  renderInPortal?: boolean;
  // Caps the popover's height in px (scrolls internally past that). When
  // omitted the cap is 192px in portal mode and the 288px (max-h-72) built-in
  // otherwise.
  maxHeight?: number;
};

/**
 * grouped / table keep the legacy custom render and forbid the
 * minimalist-only props at the type level.
 */
type RestVariantProps = CommonProps & {
  variant: 'grouped' | 'table';
  hasSearch?: never;
  loading?: never;
  label?: never;
  required?: never;
  error?: never;
  errorMessage?: never;
  renderInPortal?: never;
  maxHeight?: never;
};

type IProps = DefaultVariantProps | RestVariantProps;

const DEFAULT_I18N: ITranslator = {
  exists: () => false,
  t: (key: string) => key,
};

const Select = ({
  variant = 'default',
  fetchFunction,
  onChange,
  updateFunction,
  loadingText = '...loading',
  errorFetchingText = 'Error loading options',
  i18n = DEFAULT_I18N,
  disabled,
  native,
  dataCy = 'c-select',
  hasSearch = false,
  loading: externalLoading = false,
  onBlur,
  label,
  required,
  error = false,
  errorMessage,
  renderInPortal = false,
  maxHeight,
  ...props
}: IProps) => {
  const componentName = 'c-Select';
  const c = useClassHelper(componentName);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [errorFetching, setErrorFetching] = useState<boolean>(false);
  const [optionsFromResource, setOptionsFromResource] = useState<IOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<Partial<IOption>>(
    props.placeholder
      ? { id: props.placeholder, description: props.placeholder }
      : { description: props.defaultValue },
  );
  const [customSelectOpen, setCustomSelectOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedOption, setFocusedOption] = useState<string | null>(null);
  const selectRef = useRef(null);
  const componentRef = useRef(null);
  const optionsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const pendingFocusIndex = useRef<number | null>(null);
  const [portalPosition, setPortalPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (customSelectOpen && componentRef.current) {
      const listener = event => {
        const isOutsideControl = !componentRef.current.contains(event.target);
        const isOutsidePopover =
          !popoverRef.current || !popoverRef.current.contains(event.target);
        if (isOutsideControl && isOutsidePopover) {
          setCustomSelectOpen(false);
        }
      };
      document.addEventListener('click', listener);
      return () => document.removeEventListener('click', listener);
    }
  }, [customSelectOpen]);

  // Portal mode: the popover is appended to document.body, so it must be
  // positioned manually from the trigger's viewport rect (top-full/mt-2 only
  // work when the popover stays a normal-flow sibling of the trigger).
  useLayoutEffect(() => {
    if (!renderInPortal || !customSelectOpen || !triggerRef.current) {
      return;
    }

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setPortalPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [renderInPortal, customSelectOpen]);

  // Clear the search term whenever the dropdown closes.
  useEffect(() => {
    if (!customSelectOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearchTerm('');
    }
  }, [customSelectOpen]);

  const errorClass = errorFetching ? '--error' : '';

  const disabledClassName = cn({
    '!opacity-50 !cursor-not-allowed': disabled,
  });

  const useTranslation = useCallback(
    (value: string, defaultValue: string): string => {
      return i18n.exists(value) ? i18n.t(value) : defaultValue;
    },
    [i18n],
  );
  const [children, options] = useMemo(() => {
    if (optionsFromResource.length) {
      const elements = optionsFromResource.map((option: IOption) => {
        return (
          <SelectOption key={option.id} value={option.id}>
            {useTranslation(option.id, option.description)}
          </SelectOption>
        );
      });

      return [elements, optionsFromResource];
    }

    const children = Children.toArray(props.children)
      .filter(child => {
        return React.isValidElement(child) && child.type === SelectOption;
      })
      .map(child => {
        const props = (
          child as React.ReactElement<{
            value: string;
            children?: string;
          }>
        ).props;
        return (
          <SelectOption key={props.value} {...props}>
            {useTranslation(props.value, props.children)}
          </SelectOption>
        );
      });

    const options = children.map(child => {
      return { id: child.props.value, description: child.props.children };
    });

    const defaultVal = {
      id: options?.[0]?.id,
      description: options?.[0]?.description,
    };

    let val = defaultVal;

    if (props.placeholder) {
      val = { id: props.placeholder, description: props.placeholder };
    }

    if (props.value) {
      const findedOption = options.find(option => option.id === props.value);
      val = findedOption ?? defaultVal;
    }

    // eslint-disable-next-line react-hooks/set-state-in-render
    setSelectedOption(val);

    return [children, options];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsFromResource, props.value, props.children]);

  useEffect(() => {
    if (fetchFunction) {
      const fetchData = async () => {
        setIsFetching(true);
        setErrorFetching(false);
        try {
          const data = await fetchFunction();
          setOptionsFromResource(data);
          if (props.defaultValue) {
            setSelectedOption({
              id: props.defaultValue,
              description: props.defaultValue,
            });
          } else {
            setSelectedOption(
              props.placeholder
                ? { id: props.placeholder, description: props.placeholder }
                : { id: data?.[0]?.id, description: data?.[0]?.description },
            );
          }
        } catch {
          setErrorFetching(true);
        }
        setIsFetching(false);
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the minimalist popover opens via keyboard, move focus to the
  // selected/first option. With hasSearch the search input autofocuses
  // instead, so we leave focus there.
  useEffect(() => {
    if (customSelectOpen && !hasSearch && pendingFocusIndex.current !== null) {
      optionsRefs.current[pendingFocusIndex.current]?.focus?.();
    }
    if (!customSelectOpen) {
      pendingFocusIndex.current = null;
    }
  }, [customSelectOpen, hasSearch]);

  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (selectRef.current) {
        const index = event.currentTarget.dataset.index;
        selectRef.current.selectedIndex = parseInt(index, 10);
        const evt = new Event('change', {
          bubbles: true,
          cancelable: false,
        });

        selectRef.current.dispatchEvent(evt);
      }
    },
    [selectRef],
  );

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      if (updateFunction) {
        updateFunction(event.target.value);
      }

      const option = event.target.options[event.target.selectedIndex];

      setSelectedOption({ id: option.value, description: option.text });
      setCustomSelectOpen(false);

      if (onChange) {
        onChange(event);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateFunction],
  );

  const handleToggle = useCallback(() => {
    if (disabled) {
      return;
    }

    setCustomSelectOpen(!customSelectOpen);
  }, [customSelectOpen, disabled]);

  const renderCustomSelect = useCallback(() => {
    if (!native) {
      return (
        <div className={c('__custom-select')}>
          <div
            className={`${componentName} ${c(variants[variant])} ${
              customSelectOpen ? 'open' : ''
            } ${disabledClassName} ${cn({
              '!bg-[var(--color-input-background-disabled)]': disabled,
            })}`}
            onClick={handleToggle}
          >
            {useTranslation(selectedOption.id, selectedOption.description)}
          </div>
          <ul
            aria-hidden={customSelectOpen ? 'false' : 'true'}
            className={c(
              customSelectOpen
                ? '__custom-select--open'
                : '__custom-select--closed',
            )}
          >
            {options.map((option, index) => (
              <li
                key={option.id}
                data-value={option.id}
                data-index={index}
                data-selected={
                  option.id === selectedOption?.id ? 'true' : undefined
                }
                onClick={handleOnClick}
                data-cy={`${dataCy}-option-${index}`}
              >
                {useTranslation(option.id, option.description)}
              </li>
            ))}
          </ul>
        </div>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customSelectOpen,
    selectedOption,
    options,
    disabledClassName,
    variant,
    native,
    useTranslation,
  ]);

  /**
   * Minimalist render — used ONLY by the `default` variant. Button trigger +
   * animated-less popover with optional search, bold + check on the selected
   * option. Selection still flows through the hidden native <select> so the
   * public `onChange` / <SelectOption> API stays untouched.
   */
  const renderMinimalistSelect = () => {
    const isPlaceholder =
      !!props.placeholder && selectedOption?.id === props.placeholder;

    const triggerText = isPlaceholder
      ? props.placeholder
      : useTranslation(selectedOption.id, selectedOption.description);

    const visibleOptions = options
      .map((option, index) => ({ ...option, index }))
      .filter(option => {
        if (!hasSearch || !searchTerm) {
          return true;
        }
        const label = useTranslation(option.id, option.description);
        const term = searchTerm.toLowerCase();
        return (
          String(label).toLowerCase().includes(term) ||
          String(option.id).toLowerCase().includes(term)
        );
      });

    const selectOption = (index: number) => {
      if (selectRef.current) {
        selectRef.current.selectedIndex = index;
        selectRef.current.dispatchEvent(
          new Event('change', { bubbles: true, cancelable: false }),
        );
      }
    };

    const handleOptionKeyDown = (
      e: KeyboardEvent<HTMLDivElement>,
      position: number,
    ) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = position < visibleOptions.length - 1 ? position + 1 : 0;
        setFocusedOption(visibleOptions[next].id);
        optionsRefs.current[next]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = position > 0 ? position - 1 : visibleOptions.length - 1;
        setFocusedOption(visibleOptions[prev].id);
        optionsRefs.current[prev]?.focus();
      } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        selectOption(visibleOptions[position].index);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setCustomSelectOpen(false);
        triggerRef.current?.focus();
      }
    };

    const controlId = props.name || dataCy;
    const listboxId = `${controlId}-listbox`;
    const getOptionId = (optionValue: string) =>
      `${controlId}-option-${optionValue}`;
    const activeDescendant =
      customSelectOpen && focusedOption
        ? getOptionId(focusedOption)
        : undefined;

    const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) {
        return;
      }

      const openAt = (fallbackIndex: number) => {
        const selectedIndex = options.findIndex(
          option => option.id === selectedOption?.id,
        );
        const index = selectedIndex >= 0 ? selectedIndex : fallbackIndex;
        pendingFocusIndex.current = index;
        setFocusedOption(options[index]?.id ?? null);
        setCustomSelectOpen(true);
      };

      if (
        !customSelectOpen &&
        (e.key === 'ArrowDown' ||
          e.key === 'Enter' ||
          e.key === ' ' ||
          e.key === 'Spacebar')
      ) {
        e.preventDefault();
        openAt(0);
      } else if (!customSelectOpen && e.key === 'ArrowUp') {
        e.preventDefault();
        openAt(options.length - 1);
      } else if (customSelectOpen && e.key === 'Escape') {
        e.preventDefault();
        setCustomSelectOpen(false);
      }
    };

    return (
      <div className="relative w-full">
        <button
          ref={triggerRef}
          type="button"
          id={controlId}
          className={cn(
            `
              c-Select__trigger
              relative
              py-0
              ps-4
              pe-9
              flex
              gap-x-2
              text-nowrap
              w-full
              cursor-pointer
              bg-white
              border
              border-gray-300
              rounded-lg
              text-start
              min-h-10
              items-center
              disabled:opacity-50
              disabled:cursor-default
              disabled:bg-(--color-input-background-disabled)
              focus-visible:focus-ring-form
            `,
            {
              // The trigger never carried an error border — `error` only
              // rendered the message block below the control.
              'border-(--color-input-border-error)': error && !customSelectOpen,
              // Open highlight: fixed blue-700 across brands (user decision,
              // UES-12493). Declared without !error on purpose — while the
              // list is open the active state outranks the error state.
              'border-blue-700': customSelectOpen,
            },
          )}
          title={isPlaceholder ? undefined : triggerText}
          aria-haspopup="listbox"
          aria-expanded={customSelectOpen ? 'true' : 'false'}
          aria-controls={customSelectOpen ? listboxId : undefined}
          disabled={disabled}
          aria-disabled={disabled}
          onClick={handleToggle}
          onKeyDown={handleTriggerKeyDown}
          data-cy={`${dataCy}-trigger`}
        >
          <span
            className={cn(
              'flex-1 min-w-0 truncate text-base text-[var(--color-font-primary)]',
              {
                // gray-500 to match InputField's ::placeholder — one
                // placeholder color across every form control (UES-12493).
                'text-gray-500': isPlaceholder,
              },
            )}
          >
            {triggerText}
          </span>
        </button>
        {externalLoading ? (
          <div className="absolute right-[15px] top-1/2 -translate-y-1/2">
            <Loading size={12} />
          </div>
        ) : (
          <div
            // Stable hook so print/PDF stylesheets can hide the caret — keep in
            // sync with the report print styles targeting c-Select__trigger-caret.
            className={cn(
              `${componentName}__trigger-caret`,
              'absolute top-1/2 inset-e-3 -translate-y-1/2 pointer-events-none',
              {
                'text-gray-400': disabled,
              },
            )}
          >
            <CaretDown size={18} aria-hidden="true" />
          </div>
        )}

        {customSelectOpen &&
          (() => {
            const popoverBody = (
              <>
                {hasSearch && (
                  <div className="bg-white p-2 -mx-1 sticky top-0 z-10">
                    <InputField
                      id="select-search"
                      type="search"
                      placeholder="Search..."
                      name="search"
                      onChange={e => setSearchTerm(e.target.value)}
                      value={searchTerm}
                      onClear={() => setSearchTerm('')}
                      autoFocus
                    />
                  </div>
                )}
                {visibleOptions.length > 0 ? (
                  visibleOptions.map((option, position) => {
                    const isSelected = selectedOption?.id === option.id;
                    const isFocused = focusedOption === option.id;
                    return (
                      <div
                        key={option.id}
                        id={getOptionId(option.id)}
                        ref={el => {
                          optionsRefs.current[position] = el;
                        }}
                        role="option"
                        aria-selected={isSelected}
                        title={useTranslation(option.id, option.description)}
                        tabIndex={isFocused ? 0 : -1}
                        data-value={option.id}
                        data-index={option.index}
                        data-cy={`${dataCy}-option-${option.index}`}
                        className={cn(
                          'relative cursor-pointer py-2 ps-4 pe-9 w-full text-sm text-gray-800 hover:bg-gray-100 rounded-lg focus:outline-none focus:bg-gray-100 focus-visible:focus-ring-form-inset',
                          {
                            'bg-gray-100': isSelected || isFocused,
                          },
                        )}
                        onClick={handleOnClick}
                        onKeyDown={e => handleOptionKeyDown(e, position)}
                        onMouseEnter={() => setFocusedOption(option.id)}
                      >
                        <Paragraph tag="span" size="sm" isBolder={isSelected}>
                          {useTranslation(option.id, option.description)}
                        </Paragraph>
                        {isSelected && (
                          <span className="absolute end-3 top-1/2 -translate-y-1/2 text-blue-700">
                            <Check size={18} />
                          </span>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="py-2 px-4 text-sm text-gray-500 text-center">
                    No results found
                  </div>
                )}
              </>
            );

            if (renderInPortal) {
              if (!portalPosition) {
                return null;
              }

              return createPortal(
                <div
                  ref={popoverRef}
                  id={listboxId}
                  role="listbox"
                  aria-labelledby={controlId}
                  aria-activedescendant={activeDescendant}
                  style={{
                    position: 'fixed',
                    top: portalPosition.top,
                    left: portalPosition.left,
                    width: portalPosition.width,
                    maxHeight: maxHeight ?? 192,
                  }}
                  className={cn(
                    'p-1 space-y-0.5 z-20 w-max max-w-[calc(100vw-2rem)] bg-white border border-gray-300 rounded-lg overflow-hidden overflow-y-auto',
                    {
                      'pt-0': hasSearch,
                    },
                  )}
                >
                  {popoverBody}
                </div>,
                document.body,
              );
            }

            return (
              <div
                id={listboxId}
                role="listbox"
                aria-labelledby={controlId}
                aria-activedescendant={activeDescendant}
                style={maxHeight ? { maxHeight } : undefined}
                className={cn(
                  'absolute mt-2 max-h-72 p-1 space-y-0.5 z-20 min-w-full w-max max-w-[calc(100vw-2rem)] bg-white border border-gray-300 rounded-lg overflow-hidden overflow-y-auto top-full',
                  {
                    'pt-0': hasSearch,
                  },
                )}
              >
                {popoverBody}
              </div>
            );
          })()}
      </div>
    );
  };

  if (isFetching) {
    return (
      <select className={`${componentName} ${c(variants[variant])}`} disabled>
        <option value="">{loadingText}</option>
        <SelectOption>{loadingText}</SelectOption>
      </select>
    );
  }

  if (errorFetching) {
    return (
      <select
        className={`${componentName} ${c(variants[variant], errorClass)}`}
        disabled
      >
        <option value="">{errorFetchingText}</option>
        <SelectOption>{errorFetchingText}</SelectOption>
      </select>
    );
  }

  const isMinimalist = !native && variant === 'default';

  // Minimalist (default variant): clean tree, no legacy SCSS wrapper. The
  // hidden native <select> only drives the change event; the visible control
  // comes from renderMinimalistSelect. `w-full` keeps it full width even when
  // the parent lays children out with flex.
  if (isMinimalist) {
    const controlId = props.name || dataCy;
    const errorId = `input_${controlId}_ErrorMessage`;

    return (
      <div
        className={`relative w-full ${disabledClassName}`}
        ref={componentRef}
        data-cy={`${dataCy}-wrapper`}
        onBlur={event => {
          // focusout bubbles; only fire when focus leaves the whole control,
          // not when it moves between trigger and options. In portal mode the
          // popover is no longer a DOM descendant of this wrapper, so it must
          // be checked separately.
          const relatedTarget = event.relatedTarget as Node | null;
          if (event.currentTarget.contains(relatedTarget)) {
            return;
          }
          if (renderInPortal && popoverRef.current?.contains(relatedTarget)) {
            return;
          }
          onBlur?.(event);
        }}
      >
        {label && (
          <InputField.Label id={controlId} required={required}>
            {label}
          </InputField.Label>
        )}
        <select
          ref={selectRef}
          onChange={handleOnChange}
          disabled={disabled}
          data-cy={`${dataCy}-select`}
          hidden
          aria-hidden="true"
          tabIndex={-1}
          {...props}
        >
          {children}
        </select>
        {/* eslint-disable-next-line react-hooks/refs */}
        {renderMinimalistSelect()}
        {(error || errorMessage) && (
          <InputField.ErrorMessage
            id={errorId}
            error={error}
            errorMessage={errorMessage}
            dataCyError={controlId}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`${c(
        '__wrapper',
        `__wrapper${variants[variant]}`,
      )} ${disabledClassName}`}
      ref={componentRef}
      data-cy={`${dataCy}-wrapper`}
    >
      <div className={c('__wrapper-inner')}>
        <select
          ref={selectRef}
          className={`${componentName} ${c(
            variants[variant],
          )} focus-visible:focus-ring-form`}
          onChange={handleOnChange}
          onBlur={onBlur}
          disabled={disabled}
          aria-disabled={disabled ? 'true' : 'false'}
          data-cy={`${dataCy}-select`}
          aria-expanded={
            native ? undefined : customSelectOpen ? 'true' : 'false'
          }
          {...props}
        >
          {children}
        </select>
        <span className={`${c('__wrapper-inner-caret')}`} aria-hidden>
          <CaretDown size={18} />
        </span>
      </div>
      {renderCustomSelect()}
    </div>
  );
};

Select.Option = SelectOption;

export default Select;
